import './CircuitsList.scss';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import _ from 'lodash';
import classNames from 'classnames';

import { List, Map } from 'immutable';
import DataTable from 'src/component/UI/DataTable';
import { getDevicesSelectedFilters } from 'src/module/filter/selector';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActiveSharp';
import { Badge } from '@mui/material';
import variables from 'src/style/variable/variables.module.scss';

import { getCircuits, getLoadingCircuits, getLoadingExportCircuits, getNumCircuitPages, getTotalCircuits } from 'src/module/circuit/selector';
import { fetchCircuits, fetchCircuitsExport } from 'src/module/circuit/action';

import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import DeviceLink from 'src/component/UI/DeviceLink';
import { formatAlarmLabel } from 'src/component/AlarmsList';
import { getAlarmSummary } from 'src/module/device/selector';
import Tooltip from '@mui/material/Tooltip';
import Button from 'src/component/UI/Button';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadSharp';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleSharp';
import { getSingularWorkTicketLabel, getTablePagePreferences } from 'src/module/authentication/selector';
import LoadingSpinner from './LoadingSpinner';
import { useTranslation } from 'src/i18n';


export const alarmsByStatusLegendLabels = Map({
  'low temp above 5': { label: 'Low Temp Above 5° C' },
  'low temp below 5': { label: 'Low Temp Below 5° C' },
  'low temp above 0': { label: 'Low Temp Above 0° C' },
  'comm fail': { label: 'Comm Fail' },
  'low load': { label: `Low Load` },
  'hi load': { label: `High Load` },
  'rtd fail': { label: `RTD Fail` },
  'gfi': { label: 'GFI' },
  'controller comm fail': { label: `Controller Comm Fail` },
  'switch fail': { label: `Switch Fail` },
  'low temp below 0': { label: `Low Temp Below 0° C` },
  'reset': { label: `Reset` },
  'gf ct failure': { label: `GF CT Failure` },
  'other': { label: `Other` }
});

export default function CircuitsList (props) {
  const { t } = useTranslation();
  const {
    status,
    noAlarmsHasNotificationFilterOption,
    controllerIdOverride,
    panelId,
    batchId,
    circuitId,
    showUnit,
    showAlarms,
    showModel,
    showErrors,
    hideCircuit,
    hideController,
    showCombinedCircuitAndController,
    multiSelect,
    updateSelected,
    removeSelected,
    highlightedDevices,
    filterable,
    banner,
    allowRemove,
    onRemoveRow,
    allowDownload,
    selected,
    deviceErrors
  } = props;

  const selectedFilters: any = useSelector(getDevicesSelectedFilters, (prev, next) => prev?.equals(next));
  const singularWorkTicketLabel = useSelector(getSingularWorkTicketLabel);
  const userPagePreference = useSelector(getTablePagePreferences('circuitsTable'));
  const circuits: any = useSelector(getCircuits) || List();
  const loading: any = useSelector(getLoadingCircuits);
  const totalCircuits: any = useSelector(getTotalCircuits);
  const [page, setPage] = useState(0);
  const [dataTableUpdateKey, setDataTableUpdateKey] = useState(0);
  const [pageSize, setPageSize] = useState(userPagePreference);
  const [filter, setFilter] = useState(null);
  const [sort, setSort] = useState(null);
  const [noAlarmHasNotification, setNoAlarmHasNotification] = useState(false);
  const numPages: any = useSelector(getNumCircuitPages);
  const dispatch: any = useDispatch();
  const [allDevicesOnPageSelected, setAllDevicesOnPageSelected] = useState(false);
  const loadingExportCircuits: any = useSelector(getLoadingExportCircuits);

  useEffect(() => {
    if (circuits?.size) {
      const c  = circuits?.every(c => selected?.has(c?.get("id"))) ?? false;
      setAllDevicesOnPageSelected(c);
    }
  }, [selected, circuits]);

  const anyCircuitOnPageSelected = () => {
    return circuits?.some(c => selected?.has(c?.get("id"))) ?? false;
  };

  const fetchPage = (_page, _pageSize, _filter, _sort) => {
    setPage(_page);
    setPageSize(_pageSize);
    setFilter(_filter);
    setSort(_sort);
  };

  // forces the memoized datatable to re-render so it shows the correct error state
  useEffect(() => {
    setDataTableUpdateKey(dataTableUpdateKey + 1);
  }, [highlightedDevices, deviceErrors]);


  const ref_filters = useRef(null);
  const ref_others = useRef(null);
  useEffect(() => {
    const others = `${controllerIdOverride} ${circuitId} ${panelId} ${page} ${filter} ${sort} ${pageSize} ${batchId}`;
    if (!_.isEqual(ref_filters.current, selectedFilters.toJS()) || others !== ref_others.current) {
      ref_filters.current = selectedFilters.toJS();
      ref_others.current = others;
      const optionalFilters = {
        controllerId: controllerIdOverride,
        panelId,
        circuitId,
        batchId,
        userSearchInput: filter,
        status: status || null,
        sort,
        noAlarmHasNotification,
        withAlarmSummary: true
      };
      dispatch(fetchCircuits(selectedFilters, Map(optionalFilters), page, pageSize));
    }
  }, [selectedFilters, controllerIdOverride, circuitId, panelId, page, filter, sort, pageSize, batchId]);


  const handleUpdateFilter = () => {
    const optionalFilters = {
      controllerId: controllerIdOverride, panelId, circuitId, batchId, userSearchInput: filter, status: status || null,
      noAlarmHasNotification: !noAlarmHasNotification
    };
    dispatch(fetchCircuits(selectedFilters, Map(optionalFilters), page, pageSize));
    setNoAlarmHasNotification(!noAlarmHasNotification);
  };

  let title = t('circuits.title');

  if (status === 'offline') {
    title = t('circuits.offlineCircuits');
  }

  const alarm = (device_id) => {
    const alarms: any = useSelector(getAlarmSummary(device_id));
    const numAlarms = (alarms?.match(/,/g) || []).length + 1;
    if (alarms === "") {
      return "";
    }
    if (numAlarms < 3) {
      return formatAlarmLabel(alarms, t);
    }
    //  icon with badge (#) with the amount of alarms
    return (
      <Tooltip title={`${formatAlarmLabel(alarms, t)}`} className='alarm' data-testid='device-link'>
        <div className='alarm-badge'>
          <Badge badgeContent={numAlarms} color="primary" className='alarmBadge'>
            <NotificationsActiveIcon fontSize='large' className='bell' />
          </Badge>
        </div>
      </Tooltip>
    );
  };

  const handleClick = () => {
    dispatch(fetchCircuitsExport(selectedFilters, Map({ panelId, controllerId: controllerIdOverride })));
  };

  const handleSelectAllOnPageClick = () => {
    const ids = circuits.map(c => c.get("id"));
    //user has cleared select all status
    if (!allDevicesOnPageSelected) {
      const updatedSet = new Set([...selected, ...ids]);
      updateSelected(updatedSet);
      return;
    }
    setAllDevicesOnPageSelected(false);
    removeSelected(ids);
  };

  const handleSelectAllClick = () => {
    const ids = circuits.map(c => c.get("id"));
    const updatedSet = new Set([...selected, ...ids]);
    updateSelected(updatedSet);
  };

  const handleClearAllClick = () => {
    updateSelected(new Set([]));
    setAllDevicesOnPageSelected(false);
  };

  const handleClickCheck = (event: React.MouseEvent<unknown>, id: number) => {
    const updatedSelected = new Set(selected);

    if (updatedSelected.has(id)) {
      updatedSelected.delete(id);
    } else {
      updatedSelected.add(id);
    }

    updateSelected(updatedSelected);
  };

  const isSelected = (id) => {
    return selected.has(id);
  };

  const getClass = (rec) => {
    if (rec && rec.getIn && deviceErrors) {
      if ( deviceErrors?.getIn([rec.get('id'), 'status', 'hasErrors'])) {
        return 'row-error';
      }
      if ( deviceErrors?.getIn([rec.get('id'), 'status', 'isWarning'])) {
        return 'row-error-warn';
      }
    }

    return '';
  };

  const iconStyles = { color: variables.primarySolid };

  const getControllerTag = (value) => {
    return value.get('parent_type') === 'commLoop' ? null : value.get('parent_tag');
  };

  const handleRemoveRow = (id) => {
    onRemoveRow(id);
  };

  const dataColumns = [];

  // Modern checkbox styling - matching maintenance screen
  const checkboxStyles = {
    padding: '8px',
    borderRadius: '8px',
    transition: 'all 0.2s ease',
    '& .MuiSvgIcon-root': {
      fontSize: 22,
      borderRadius: '6px',
    },
    '&:hover': {
      backgroundColor: 'rgba(25, 118, 210, 0.08)',
    },
    '&.Mui-checked': {
      color: '#1976d2',
      '& .MuiSvgIcon-root': {
        filter: 'drop-shadow(0 2px 4px rgba(25, 118, 210, 0.3))',
      },
    },
    '&.MuiCheckbox-indeterminate': {
      color: '#1976d2',
    },
  };

  if (multiSelect) {
    dataColumns.push({
      id: 'select-column',
      Header: () => <Checkbox
        checked={allDevicesOnPageSelected}
        indeterminate={!allDevicesOnPageSelected && anyCircuitOnPageSelected()}
        onChange={handleSelectAllOnPageClick}
        sx={checkboxStyles}
      />,
      accessor: (row: any) => row.id,
      sortable: false,
      isSorted: false,
      disableSortBy: true,
      ClassName: (row: any) => {
        const id = row.original.get('id');
        return getClass(id);
      },
      Cell: (row: any) => {
        const isCheckselected = isSelected(row.cell.row.original.get('id'));
        return (
          <Checkbox
            checked={isCheckselected}
            onClick={(event) => handleClickCheck(event, row.cell.row.original.get('id'))}
            sx={checkboxStyles}
          />
        );
      }
    });
  }

  if (!hideController) {
    dataColumns.push({
      id: 'controller.tag',
      Header: t('circuits.controller'),
      accessor: (row: any) => {
        if (row && row.get) {
          return row?.get('parent_tag');
        }
        return 'N/A';
      },
      sortable: true,
      ClassName: (row: any) => {
        return getClass(row.original);
      },
      Cell: (row: any) => {
        return (
          <DeviceLink label={row.cell.value} controllerTag={getControllerTag(row.cell.row.original)} />
        );
      }
    });
  }
  if (!hideCircuit) {
    dataColumns.push({
      id: 'devices.tag',
      Header: t('circuits.circuit'),
      accessor: (row: any) => {
        if (row && row.get) {
          return row.get('tag');
        }
        return '';
      },
      ClassName: (row: any) => {
        return getClass(row.original);
      },
      sortable: true,
      Cell: (row: any) => {
        return (
          <DeviceLink label={row.cell.value} circuitTag={row.cell.value} rawTag={row.cell.row.original.get('raw_tag')} controllerTag={getControllerTag(row.cell.row.original)} />
        );
      }
    });
  }
  if (showCombinedCircuitAndController) {
    dataColumns.push({
      id: 'controller_tag',
      Header: t('circuits.controllerCircuit'),
      accessor: (row: any) => row?.get('tag'),
      maxWidth: '200px',
      Cell: (row: any) => {
        return (
          <>
            <DeviceLink label={row.cell.value} controllerTag={row.cell.row.original.get('parent_tag')} /> /
            {row.cell.value ? (<DeviceLink label={row.cell.value} circuitTag={row.cell.value} controllerTag={getControllerTag(row.cell.row.original)} rawTag={row.cell.row.original.get('raw_tag')} />) : 'N/A'}
          </>
        );
      },
      ClassName: (row: any) => {
        const classes = [];
        const id = row.original?.get('id');
        if (row.original.get('critical') === 1) {
          classes.push('critical');
        }
        if (getClass(id)) {
          classes.push(getClass(id));
        }
        return classes.join(' ');
      }
    });
  }

  if (showUnit) {
    dataColumns.push(
      {
        id: 'devices.unit',
        Header: t('common.unit'),
        accessor: (row: any) => row?.get('unit'),
        sortable: true,
        ClassName: (row: any) => {
          return getClass(row.original);
        },
        Cell: (row: any) => { return row.cell.value ? row.cell.value : ''; }
      },
    );
  }
  if (showAlarms) {
    dataColumns.push(
      {
        id: 'devices.alarms',
        Header: t('nav.alarms'),
        accessor: (row: any) => row?.get('id'),
        disableSortBy: true,
        ClassName: (row: any) => {
          return getClass(row.original);
        },
        Cell: (row: any) => { return alarm(row.cell.value) || ''; }
      },
    );
  }
  if (showModel) {
    dataColumns.push(
      {
        id: 'devices.model',
        Header: t('circuits.model'),
        accessor: (row: any) => row?.get('model'),
        sortable: true,
        ClassName: (row: any) => {
          return getClass(row.original);
        },
        Cell: (row: any) => { return row.cell.value ? row.cell.value : ''; }
      }
    );
  }
  if (showErrors) {
    dataColumns.push(
      {
        id: 'devices.errors',
        Header: t('common.error'),
        sortable: false,
        ClassName: (row: any) => {
          return getClass(row.original);
        },
        Cell: (row: any) => {
          const errs =  deviceErrors?.getIn([row?.cell?.row?.original?.get("id"), 'status', 'errors']);
          if (List.isList(errs)) {
            return (errs as List<string>).map(err => t(err)).toArray();
          }
          return errs;

        }
      }
    );
  }

  if (allowRemove) {
    dataColumns.push(
      {
        id: 'remove_all',
        disableSortBy: true,
        ClassName: (row: any) => {
          return getClass(row.original);
        },
        accessor: (row: any) => {
          return <div className='error-actions'><Button onClick={() => { handleRemoveRow(row?.get('id')); }} icon={<RemoveCircleOutlineIcon style={{ fontSize: 14 }} />} /></div>;
        },
      },
    );
  }
  return (
    <div className='circuits-list' data-testid='circuits-list'>
      <h3>{title}{allowDownload ? <Button loading={loadingExportCircuits} spinner={<LoadingSpinner size={14} color={variables.primary}/>} onClick={() => handleClick()} icon={<FileDownloadOutlinedIcon style={iconStyles} />}></Button> : null}</h3>
      <DataTable
        filterable={filterable}
        bannerComponent={banner ? <div
          className={'data-table-banner'}
        >
          <div className='data-table-inner'>
            <div>{banner.replace('%num_circuits%', selected.size)}</div>
            <span className='data-table-pipe' >|</span>
            {
              selected.size ?
                <>
                  <div className={classNames(!allDevicesOnPageSelected && "clickable-text", allDevicesOnPageSelected && 'disabled-text')} onClick={handleSelectAllClick}>
                    {t('common.selectAll')}
                  </div>
                  <Button className='clear-button' onClick={handleClearAllClick}>{t('common.clear')}</Button>
                </>
                : null
            }
          </div>
        </div> : null}
        filterLabel={t('circuits.searchCircuits')}
        columns={dataColumns}
        data={circuits}
        unit={selectedFilters.get('unit')}
        pagination
        numPages={numPages}
        totalRecords={totalCircuits}
        loading={loading}
        fetchPage={fetchPage}
        tableId='circuitsTable'
        columnWidths={['10px', 'auto', 'auto', 'auto', 'auto', 'auto']}
        key={dataTableUpdateKey}
      />

      {(noAlarmsHasNotificationFilterOption) ? (
        <FormControlLabel
          control={
            <Checkbox
              checked={noAlarmHasNotification}
              onChange={handleUpdateFilter}
              name="noAlarmCheck"
              color="primary"
            />
          }
          label={t('circuits.withTicketNoAlarms', { label: singularWorkTicketLabel || t('workTickets.singular') })}
        />) : null}

    </div>
  );
}

CircuitsList.propTypes = {
  pagination: PropTypes.bool,
  includeArchived: PropTypes.bool,
  showSetting: PropTypes.bool,
  showValue: PropTypes.bool,
  showUnit: PropTypes.bool,
  showModel: PropTypes.bool,
  showAlarms: PropTypes.bool,
  showAlarmRaisedAt: PropTypes.bool,
  showErrors: PropTypes.bool,
  hideCircuit: PropTypes.bool,
  hideController: PropTypes.bool,
  showCombinedCircuitAndController: PropTypes.bool,
  suppressHeader: PropTypes.bool,
  suppressSearch: PropTypes.bool,
  noAlarmsHasNotificationFilterOption: PropTypes.bool,
  status: PropTypes.string,
  controllerIdOverride: PropTypes.string,
  panelId: PropTypes.string,
  batchId: PropTypes.string,
  circuitId: PropTypes.string,
  multiSelect: PropTypes.bool,
  updateSelected: PropTypes.func,
  removeSelected: PropTypes.func,
  onSelectAllOnPage: PropTypes.func,
  onSelectAll: PropTypes.func,
  onRemoveRow: PropTypes.func,
  handleselectedDeviceCountChanged: PropTypes.func,
  selected: PropTypes.instanceOf(Set),
  isAllselectedOnPage: PropTypes.bool,
  isAllselected: PropTypes.bool,
  filterable: PropTypes.bool,
  allowRemove: PropTypes.bool,
  allowDownload: PropTypes.bool,
  highlightedDevices: PropTypes.object,
  banner: PropTypes.string,
  deviceErrors: PropTypes.object,
};

CircuitsList.defaultProps = {
  pagination: false,
  includeArchived: false,
  showSetting: false,
  showValue: false,
  showUnit: false,
  showAlarms: false,
  showModel: false,
  showAlarmRaisedAt: false,
  showErrors: false,
  hideCircuit: false,
  hideController: false,
  showCombinedCircuitAndController: false,
  suppressHeader: false,
  suppressSearch: false,
  multiSelect: false,
  isAllselected: false,
  allowRemove: false,
  filterable: true,
  allowDownload: true,
  selected: new Set([]),
  highlightedDevices: List(),
  deviceErrors: Map(),
  handleselectedDeviceCountChanged: () => {},
  updateSelected: () => { },
  removeSelected: () => { },
  onSelectAllOnPage: () => { },
  onRemoveRow: () => { },
  onSelectAll: () => { },
};
